import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, CheckCircle, ArrowLeft, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { RebuildNotice } from '../components/RebuildNotice';
import { getOffer } from '../lyphline';
import { useSettings } from '../contexts/SettingsContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RepairQuote = () => {
  const { settings } = useSettings();
  const CURRENT_OFFER = getOffer(settings);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Contact Info
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    province: '',
    country: '',
    
    // Machine Info
    brand: '',
    model: '',
    size: '',
    serial_number: '',
    purchase_date: '',
    services: [],
    
    // Issue Details
    issue_type: '',
    issue_description: '',
    error_codes: '',
    
    // Additional
    preferred_contact: 'email',
    urgency: 'standard',
    rush: false,
    promo_code: ''
  });

  const serviceOptions = ['Rebuilds', 'Modifications', 'Parts / Components', 'Financing'];

  const toggleService = (svc) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(svc)
        ? prev.services.filter(s => s !== svc)
        : [...prev.services, svc]
    }));
  };

  const machinesBrands = [
    'Residential / home-scale',
    'Small business',
    'Semi-commercial / production',
    'Other / not sure'
  ];

  const machineSizes = [
    'Small / Compact',
    'Medium / Standard',
    'Large / Pro',
    'Extra Large / Commercial',
    'Industrial',
    'Not Sure'
  ];

  const issueTypes = [
    'Vacuum pump not working',
    'Not reaching temperature',
    'Cycle not completing',
    'Error code displayed',
    'Strange noises',
    'Leaking',
    'Control panel issues',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/repair-quotes`, formData);
      setSubmitted(true);
      toast.success('Rebuild quote request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="pt-36 pb-20">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="bg-white rounded-2xl p-12 shadow-sm">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-[#0a0203] mb-4">Request Received!</h1>
              <p className="text-gray-600 mb-8">
                Thank you for your rebuild quote request. Our team will review your information and 
                contact you within 1-2 business days with a rebuild estimate.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button variant="outline" data-testid="quote-back-home">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
                  </Button>
                </Link>
                <Link to="/process-rates">
                  <Button className="bg-[#003456] hover:bg-[#023F63]" data-testid="quote-view-rates">
                    View Process &amp; Rates
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="repair-quote-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-36 pb-12 bg-gradient-to-br from-[#003456] to-[#023F63]">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-[#CE8946] rounded-xl flex items-center justify-center">
              <Wrench className="h-7 w-7 text-[#0a0203]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Free Diagnostic Quote</h1>
              <p className="text-white/70">Tell us about your freeze dryer and we'll send a diagnostic report and quote</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            <RebuildNotice />
            {CURRENT_OFFER.active && (
              <div className="rounded-xl border border-[#CE8946]/40 bg-[#CE8946]/10 p-4" data-testid="repair-offer">
                <p className="text-sm text-[#003456]"><span className="font-bold text-[#B87333]">Current offer:</span> {CURRENT_OFFER.headline}. {CURRENT_OFFER.detail}</p>
                <input name="promo_code" value={formData.promo_code} onChange={handleChange} placeholder={`Offer code (e.g. ${CURRENT_OFFER.code})`} className="w-full mt-3 px-4 py-2.5 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="repair-promo" />
              </div>
            )}
            {/* Contact Information */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-[#0a0203] mb-6">Contact Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    data-testid="repair-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    data-testid="repair-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    data-testid="repair-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company (optional)</label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company name"
                    data-testid="repair-company"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Street address"
                    data-testid="repair-address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City / Town</label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    data-testid="repair-city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Province / State</label>
                  <Input
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                    placeholder="Province or State"
                    data-testid="repair-province"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                  <Input
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                    data-testid="repair-country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method</label>
                  <select
                    name="preferred_contact"
                    value={formData.preferred_contact}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="text">Text Message</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Machine Information */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-[#0a0203] mb-6">Machine Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand / Manufacturer *</label>
                  <select
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                    data-testid="repair-brand"
                  >
                    <option value="">Select brand...</option>
                    {machinesBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model Name/Number</label>
                  <Input
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="e.g., Pro XL, Standard, etc."
                    data-testid="repair-model"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Machine Size *</label>
                  <select
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                    data-testid="repair-size"
                  >
                    <option value="">Select size...</option>
                    {machineSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number</label>
                  <Input
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleChange}
                    placeholder="If available"
                    data-testid="repair-serial"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Approximate Purchase Date</label>
                  <Input
                    type="month"
                    name="purchase_date"
                    value={formData.purchase_date}
                    onChange={handleChange}
                    data-testid="repair-purchase-date"
                  />
                </div>
              </div>
            </div>

            {/* Services Interested In */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-[#003456] mb-6">What services are you interested in?</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {serviceOptions.map((svc) => (
                  <label
                    key={svc}
                    className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                      formData.services.includes(svc)
                        ? 'border-[#CE8946] bg-[#CE8946]/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`repair-service-${svc.split(' ')[0].toLowerCase()}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.services.includes(svc)}
                      onChange={() => toggleService(svc)}
                      className="h-4 w-4 accent-[#B87333]"
                    />
                    <span className="font-medium text-[#003456] text-sm">{svc}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Issue Details */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-[#0a0203] mb-6">Issue Details</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type of Issue *</label>
                  <select
                    name="issue_type"
                    value={formData.issue_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                    data-testid="repair-issue-type"
                  >
                    <option value="">Select issue type...</option>
                    {issueTypes.map(issue => (
                      <option key={issue} value={issue}>{issue}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Describe the Problem *</label>
                  <textarea
                    name="issue_description"
                    value={formData.issue_description}
                    onChange={handleChange}
                    placeholder="Please describe the issue in detail. When did it start? What happens when you try to run a cycle? Any unusual sounds or behaviors?"
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent resize-none"
                    data-testid="repair-description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Error Codes (if any)</label>
                  <Input
                    name="error_codes"
                    value={formData.error_codes}
                    onChange={handleChange}
                    placeholder="e.g., E01, E05, etc."
                    data-testid="repair-error-codes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Urgency Level</label>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { value: 'standard', label: 'Standard', desc: '5-10 business days' },
                      { value: 'priority', label: 'Priority', desc: '3-5 business days' },
                      { value: 'urgent', label: 'Urgent', desc: '1-2 business days' }
                    ].map(option => (
                      <label 
                        key={option.value}
                        className={`flex-1 min-w-[150px] p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                          formData.urgency === option.value 
                            ? 'border-[#003456] bg-[#f0f8ff]' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="urgency"
                          value={option.value}
                          checked={formData.urgency === option.value}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <span className="font-semibold text-[#0a0203]">{option.label}</span>
                        <span className="block text-sm text-gray-500">{option.desc}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <label
                  className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                    formData.rush ? 'border-[#CE8946] bg-[#CE8946]/10' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="repair-rush"
                >
                  <input
                    type="checkbox"
                    checked={formData.rush}
                    onChange={(e) => setFormData(prev => ({ ...prev, rush: e.target.checked }))}
                    className="h-5 w-5 accent-[#B87333]"
                    data-testid="repair-rush-checkbox"
                  />
                  <span className="text-[#003456] font-semibold text-sm">
                    🔥 I need rush turnaround
                    <span className="block text-gray-500 font-normal text-xs mt-0.5">Flag my job as urgent so your team prioritises it.</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Submit */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                * Required fields
              </p>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-[#003456] hover:bg-[#023F63] px-8 py-6 text-lg"
                data-testid="repair-submit"
              >
                {loading ? 'Submitting...' : (
                  <>
                    Submit Request <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RepairQuote;
